import { logStorageQuotaExceeded, logStorageError, logIndexedDBSaveFailed, logIndexedDBGetFailed, logIndexedDBBlobNotFound } from 'src/utils/observability/logs/webview-reload/webviewReloadLogs';

const DB_NAME = 'uploadProductForm';
const DB_VERSION = 1;
const STORE_NAME = 'images';

let dbInstance: IDBDatabase | null = null;

const isQuotaExceededError = (error: unknown): boolean => {
  return error instanceof DOMException && (
    error.name === 'QuotaExceededError' || error.code === 22
  );
};

const openDB = async (): Promise<IDBDatabase> => {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

const saveBlob = async (key: string, blob: Blob): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.put(blob, key);

      request.onerror = () => {
        const errorMessage = request.error?.message || 'Unknown IndexedDB error';
        logIndexedDBSaveFailed(key, errorMessage);
        if (isQuotaExceededError(request.error)) {
          logStorageQuotaExceeded(errorMessage);
        } else {
          logStorageError(errorMessage);
        }
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };

      transaction.onerror = () => {
        const errorMessage = transaction.error?.message || 'Transaction error';
        logIndexedDBSaveFailed(key, errorMessage);
        reject(transaction.error);
      };

      transaction.onabort = () => {
        logIndexedDBSaveFailed(key, 'Transaction aborted');
        reject(new Error('Transaction aborted'));
      };
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logIndexedDBSaveFailed(key, errorMessage);
    if (isQuotaExceededError(error)) {
      logStorageQuotaExceeded(errorMessage);
    } else {
      logStorageError(errorMessage);
    }
    throw error;
  }
};

const getBlob = async (key: string): Promise<Blob | null> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onerror = () => {
        logIndexedDBGetFailed(key, request.error?.message || 'Unknown error');
        reject(request.error);
      };
      request.onsuccess = () => {
        const result = request.result;
        if (result instanceof Blob) {
          resolve(result);
        } else {
          logIndexedDBBlobNotFound(key);
          resolve(null);
        }
      };

      transaction.onerror = () => {
        logIndexedDBGetFailed(key, transaction.error?.message || 'Transaction error');
        reject(transaction.error);
      };
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logIndexedDBGetFailed(key, errorMessage);
    return null;
  }
};

const deleteBlob = async (key: string): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch {
    // silently ignore
  }
};

const clearAll = async (): Promise<void> => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch {
    // silently ignore
  }
};

export const indexedDBUtils = {
  saveBlob,
  getBlob,
  deleteBlob,
  clearAll,
};
